import {ILccaActivityImperial, ILccaCalculatedImperial} from "@/modules/lcca/interfaces/lccaCalculated";
import {getFhwaData} from "@/modules/lcca/lib/calculations/fhwa_2010";
import {IQueuePeriod, IUserCostResults} from "@/modules/lcca/interfaces/probabilistic";
import {trafficHourlyDistributions, urbanWeekday} from "@/modules/lcca/lib/trafficDsitributions";

export const calculateUserCosts = function (scenario: ILccaCalculatedImperial,
                                            options: any,
                                            activity: ILccaActivityImperial): IUserCostResults {
  if (!scenario.userCostsInAnalysis) {
    return {
      queueLengthWarning: false,
      delayCostsPerDay: 0,
      vehicleCostsTotal: 0,
      vehicleCostsPerDay: 0,
      delayCostsTotal: 0,
      queuePeriods: [],
      traverseWzCosts: {
        cars: 0,
        trucks: 0,
        comboTrucks: 0,
        total: 0
      },
      weightedSlowCosts: {

        delay: {
          cars: 0,
          trucks: 0,
          comboTrucks: 0,
          total: 0
        },
        vehicle: {
          cars: 0,
          trucks: 0,
          comboTrucks: 0,
          total: 0
        }
      },
      weightedStoppedCosts: {
        delay: {
          cars: 0,
          trucks: 0,
          comboTrucks: 0,
          total: 0
        },
        vehicle: {
          cars: 0,
          trucks: 0,
          comboTrucks: 0,
          total: 0
        }
      },
      totalCostsPerDay: {
        cars: 0,
        trucks: 0,
        comboTrucks: 0,
        total: 0
      },
      totalCosts: {
        cars: 0,
        trucks: 0,
        comboTrucks: 0,
        total: 0
      },
      discountedCosts: {
        cars: 0,
        trucks: 0,
        comboTrucks: 0,
        total: 0
      }
    };
  }

  // TODO: Is there a default AADT distribution we should use?
  const trafficBreakdown = {
    cars: (100 - ((scenario.singleTruckAadtPct || 0) + (scenario.combinationTruckAadtPct || 0))) / 100 || 0.89,
    trucks: (scenario.singleTruckAadtPct || 0) / 100 || 0.06,
    comboTrucks: (scenario.combinationTruckAadtPct || 0) / 100 || 0.05
  };

  const detour = 0;
  const noShow = 0;


  // TODO: trafficgrowth should be a distribution, maybe?
  const trafficGrowth = (options.annualGrowthTraffic || 0) / 100 + 1;
  const normalSpeed = scenario.speedLimitNormal_mph || 0;
  const freeFlowCapacity = (scenario.freeFlowCapacity || 0) * (scenario.numLanesNormal || 0); //vehicles/hr
  const analysisPeriod = scenario.analysisPeriodLength_yrs || 0;
  const maxQueueLength = scenario.maxQueueLength_mi;
  const trafficDelayCosts = {
    cars: (options.passengerCarsTime) || 11.58,
    trucks: (options.singleUnitTrucksTime) || 18.54,
    comboTrucks: (options.combinationTrucksTime) || 22.31
  };

  // console.log("Options", options.annualGrowthTraffic, options.passengerCarsTime, options.singleUnitTrucksTime, options.combinationTrucksTime)

  /*
  Activity data
   */
  const workzoneLength = (activity.workzoneLength_mi || 0);
  // const maintenanceFrequency = (activity.maintenanceFrequency);
  const serviceLife = (activity.serviceLife_yrs);
  // const repetitions = (Math.floor(serviceLife_yrs/maintenanceFrequency)-1);
  const aadt = (scenario.aadtAnalysisPeriod || 0);
  const startYear = (activity.startYear || 0);
  const workzoneSpeed = (activity.workzoneLength_mi || 0);
  const workzoneDuration = (activity.workzoneDuration_days) || 0;
  const workzoneCapacity = ((activity.workzoneCapacity || 0) * (activity.workzoneNumLanes || 0)); //vehicles/hr

  /*
  Calculate FHWA data
   */
  const stoppedFhwa = getFhwaData(0);
  const wzFhwa = getFhwaData(workzoneSpeed);
  const normalFhwa = getFhwaData(normalSpeed);

  const currentYearAadt = aadt * Math.pow(trafficGrowth, startYear);

  let queueLengthWarning = false;

  const trafficDistDefaults = scenario.trafficHourlyDistribution || urbanWeekday;

  const trafficDemand = trafficDistDefaults.map(function (element: number, index) {
    const trafficDemandElement = {
      baseDemand: currentYearAadt * element,
      adjustedDemand: 0,
      wz: false
    };

    if (activity.laneClosePeriods && activity.laneClosePeriods.filter(function (element: { end: number, start: number }) {
      return element.start <= index && element.end >= index;
    }).length > 0) {
      trafficDemandElement.wz = true;
    }
    return trafficDemandElement;
  });

  const demandAdjusted = trafficDemand.map(function (element) {
    element.adjustedDemand = element.baseDemand * (1 - detour - noShow);
    return element;
  });

  const queueRate = demandAdjusted.map(function (element, index) {
    return element.adjustedDemand - workzoneCapacity;
  });


  const queuedVehicles: {
    count: number,
    average: number,
    period: number,
    density: number,
    densityDifference: number,
    upstream: number,
    queueLength: number,
  }[] = [];

  demandAdjusted.forEach(function (element, index) {
    if (index == 0) {
      queuedVehicles.push({
        count: Math.max(0, queueRate[index]),
        average: Math.max(0, queueRate[index]),
        period: 0,
        upstream: 0,
        densityDifference: 0,
        density: 0,
        queueLength: 0
      });
    } else {
      queuedVehicles.push({
        count: Math.max(0, queuedVehicles[index - 1].count + queueRate[index]),
        average: (Math.max(0, queuedVehicles[index - 1].count + queueRate[index]) + queuedVehicles[index - 1].count) / 2,
        period: 0,
        density: 0,
        densityDifference: 0,
        upstream: 0,
        queueLength: 0
      });
    }
  });

  const traverseWZ = demandAdjusted.map(function (element, index) {
    const obj = {
      wz: 0,
      queue: 0,
      vcRatio: 0,
      queueSpeed: 0,
      stop: 0,
      slowDown: 0
    };

    const previousQueue = index == 0 ? 0 : queuedVehicles[index - 1].count;


    if (trafficDemand[index].wz) {
      //Why is this empty?
    }

    // If there are vehicles in the queue
    if (queuedVehicles[index].count > 0) {
      obj.queue = workzoneCapacity; // vehicles/hour
      obj.stop = element.adjustedDemand; //vehicles/hour
    } // if there were vehicles in the queue, but aren't any now
    else if (previousQueue > 0) {
      obj.queue = Math.round(Math.abs(previousQueue / queueRate[index]) * workzoneCapacity);
      obj.stop = Math.round(Math.abs(previousQueue / queueRate[index]) * element.adjustedDemand);
    } else {
      obj.queue = 0;
      obj.stop = 0;
    }

    // vehicle/capacity ratio
    obj.vcRatio = obj.queue / freeFlowCapacity;
    obj.queueSpeed = 7.705239562905 * Math.pow(obj.vcRatio, 2) + 15.291398150742 * obj.vcRatio;

    // If during workzone
    if (trafficDemand[index].wz) {
      obj.wz = Math.min(workzoneCapacity, element.adjustedDemand + previousQueue); // vehicles/hour
      obj.slowDown = element.adjustedDemand - obj.stop; // vehicles/hour
    }

    return obj;
  });

  queuedVehicles.forEach(function (element, index) {
    if (index == 0) {
      if (traverseWZ[index].queue > 0) {
        element.period = 1;
      }
    } else {
      if (traverseWZ[index].queue > 0) {
        if (trafficDemand[index].wz == trafficDemand[index - 1].wz) {
          element.period = queuedVehicles[index - 1].period;
        } else {
          element.period = queuedVehicles[index - 1].period + 1;
        }
      }
    }
  });


  queuedVehicles.forEach(function (element, index) {
    if (element.period != 0) {
      element.density = traverseWZ[index].queue / traverseWZ[index].queueSpeed;
      element.upstream = traverseWZ[index].stop / normalSpeed;
      element.densityDifference = element.density - element.upstream;
      const calculatedQueueLength = element.average / element.densityDifference;
      // Test if the Queue length is negative or greater than the maximum queue length
      if (calculatedQueueLength > 0 && calculatedQueueLength < (maxQueueLength || 0)) {
        element.queueLength = calculatedQueueLength;
      } else {
        element.queueLength = maxQueueLength || 0;
        queueLengthWarning = true;
      }
    }
  });


  const queuePeriods: IQueuePeriod[] = [];
  queuedVehicles.forEach(function (element, index) {
    queuePeriods[element.period] = queuePeriods[element.period] || {
      totalCount: 0,
      count: 0,
      groups: 0,
      queueLength: 0,
      queueSpeed: 0,
      upstreamSpeed: 0,

      avgQueuedVehicles: 0,
      avgQueueLength: 0,
      avgQueueSpeed: 0,
      avgUpstreamSpeed: 0,
      avgDelayPerVehicle: 0,
      delays: {
        cars: 0,
        trucks: 0,
        comboTrucks: 0,
        total: 0,
      }
    };

    queuePeriods[element.period].totalCount += traverseWZ[index].queue;
    queuePeriods[element.period].count += element.average;
    queuePeriods[element.period].queueLength += element.queueLength;
    queuePeriods[element.period].queueSpeed += traverseWZ[index].queueSpeed;
    queuePeriods[element.period].upstreamSpeed += normalSpeed;
    queuePeriods[element.period].groups++;
  });

  const totalSlowed = traverseWZ.reduce(function (sum, value) {
    return sum + value.slowDown;
  }, 0);
  const totalStopped = traverseWZ.reduce(function (sum, value) {
    return sum + value.stop;
  }, 0);
  const totalTraversed = traverseWZ.reduce(function (sum, value) {
    return sum + value.wz;
  }, 0);
  const totalTraversedQueue = traverseWZ.reduce(function (sum, value) {
    return sum + value.queue;
  }, 0);


  const slowBreakdown = {
    cars: totalSlowed * trafficBreakdown.cars,
    trucks: totalSlowed * trafficBreakdown.trucks,
    comboTrucks: totalSlowed * trafficBreakdown.comboTrucks,
    total: totalSlowed
  };

  const stopBreakdown = {
    cars: totalStopped * trafficBreakdown.cars,
    trucks: totalStopped * trafficBreakdown.trucks,
    comboTrucks: totalStopped * trafficBreakdown.comboTrucks,
    total: totalStopped
  };
  const traverseBreakdown = {
    cars: totalTraversed * trafficBreakdown.cars,
    trucks: totalTraversed * trafficBreakdown.trucks,
    comboTrucks: totalTraversed * trafficBreakdown.comboTrucks,
    total: totalTraversed
  };

  const wzTraverseDelay = ((workzoneLength / workzoneSpeed) - (workzoneLength / normalSpeed));

  const traverseWzDelayCosts = {
    cars: wzTraverseDelay * trafficBreakdown.cars * trafficDelayCosts.cars,
    trucks: wzTraverseDelay * trafficBreakdown.trucks * trafficDelayCosts.trucks,
    comboTrucks: wzTraverseDelay * trafficBreakdown.comboTrucks * trafficDelayCosts.comboTrucks,
  };

  const weightedSlowCosts = {
    delay: {
      cars: (normalFhwa.addedTime.cars - wzFhwa.addedTime.cars) * (slowBreakdown.cars / 1000) * trafficDelayCosts.cars,
      trucks: (normalFhwa.addedTime.trucks - wzFhwa.addedTime.trucks) * (slowBreakdown.trucks / 1000) * trafficDelayCosts.trucks,
      comboTrucks: (normalFhwa.addedTime.comboTrucks - wzFhwa.addedTime.comboTrucks) * (slowBreakdown.comboTrucks / 1000) * trafficDelayCosts.comboTrucks,
      total: 0,
    },
    vehicle: {
      cars: (normalFhwa.addedCost.cars - wzFhwa.addedCost.cars) * (slowBreakdown.cars / 1000),
      trucks: (normalFhwa.addedCost.trucks - wzFhwa.addedCost.trucks) * (slowBreakdown.trucks / 1000),
      comboTrucks: (normalFhwa.addedCost.comboTrucks - wzFhwa.addedCost.comboTrucks) * (slowBreakdown.comboTrucks / 1000),
      total: 0,
    }
  };

  const weightedStoppedCosts = {
    delay: {
      cars: (normalFhwa.addedTime.cars - stoppedFhwa.addedTime.cars) * (stopBreakdown.cars / 1000) * trafficDelayCosts.cars,
      trucks: (normalFhwa.addedTime.trucks - stoppedFhwa.addedTime.trucks) * (stopBreakdown.trucks / 1000) * trafficDelayCosts.trucks,
      comboTrucks: (normalFhwa.addedTime.comboTrucks - stoppedFhwa.addedTime.comboTrucks) * (stopBreakdown.comboTrucks / 1000) * trafficDelayCosts.comboTrucks,
      total: 0,
    },
    vehicle: {
      cars: (normalFhwa.addedCost.cars - stoppedFhwa.addedCost.cars) * (stopBreakdown.cars / 1000),
      trucks: (normalFhwa.addedCost.trucks - stoppedFhwa.addedCost.trucks) * (stopBreakdown.trucks / 1000),
      comboTrucks: (normalFhwa.addedCost.comboTrucks - stoppedFhwa.addedCost.comboTrucks) * (stopBreakdown.comboTrucks / 1000),
      total: 0,
    }
  };

  const idlingCosts = {
    cars: 0.94,
    trucks: 1.04,
    comboTrucks: 1.12
  };

  weightedSlowCosts.delay.total = weightedSlowCosts.delay.cars + weightedSlowCosts.delay.trucks + weightedSlowCosts.delay.comboTrucks;
  weightedSlowCosts.vehicle.total = weightedSlowCosts.vehicle.cars + weightedSlowCosts.vehicle.trucks + weightedSlowCosts.vehicle.comboTrucks;
  weightedStoppedCosts.delay.total = weightedStoppedCosts.delay.cars + weightedStoppedCosts.delay.trucks + weightedStoppedCosts.delay.comboTrucks;
  weightedStoppedCosts.vehicle.total = weightedStoppedCosts.vehicle.cars + weightedStoppedCosts.vehicle.trucks + weightedStoppedCosts.vehicle.comboTrucks;

  queuePeriods.forEach(function (period, index) {
    period.avgQueuedVehicles = period.count / period.groups;
    period.avgQueueLength = period.queueLength / period.groups;
    period.avgQueueSpeed = period.queueSpeed / period.groups;
    period.avgUpstreamSpeed = period.upstreamSpeed / period.groups;

    // I added the "|| 0" here because of NaNs showing up - Ben
    period.avgDelayPerVehicle = (period.avgQueueLength / period.avgQueueSpeed) - (period.avgQueueLength / period.avgUpstreamSpeed) || 0;

    period.delays = {
      cars: period.totalCount * period.avgDelayPerVehicle * trafficBreakdown.cars,
      trucks: period.totalCount * period.avgDelayPerVehicle * trafficBreakdown.trucks,
      comboTrucks: period.totalCount * period.avgDelayPerVehicle * trafficBreakdown.comboTrucks,
      total: period.totalCount * period.avgDelayPerVehicle * trafficBreakdown.cars
    };
    period.idleVocCost = {
      cars: period.delays.cars * idlingCosts.cars,
      trucks: period.delays.trucks * idlingCosts.trucks,
      comboTrucks: period.delays.comboTrucks * idlingCosts.comboTrucks,
      total: period.delays.cars * idlingCosts.cars + period.delays.trucks * idlingCosts.trucks + period.delays.comboTrucks * idlingCosts.comboTrucks
    };
    period.idleDelayCost = {
      cars: period.delays.cars * trafficDelayCosts.cars,
      trucks: period.delays.trucks * trafficDelayCosts.trucks,
      comboTrucks: period.delays.comboTrucks * trafficDelayCosts.comboTrucks,
      total: period.delays.cars * trafficDelayCosts.cars + period.delays.trucks * trafficDelayCosts.trucks + period.delays.comboTrucks * trafficDelayCosts.comboTrucks
    };
  });

  const traverseWzCosts = {
    cars: traverseBreakdown.cars * wzTraverseDelay * trafficDelayCosts.cars,
    trucks: traverseBreakdown.trucks * wzTraverseDelay * trafficDelayCosts.trucks,
    comboTrucks: traverseBreakdown.comboTrucks * wzTraverseDelay * trafficDelayCosts.comboTrucks,
    total: 0
  };
  traverseWzCosts.total = traverseWzCosts.cars + traverseWzCosts.trucks + traverseWzCosts.comboTrucks;

  const getQueuePeriodData = function (type: string, item: string) {
    const value = queuePeriods.reduce(function (sum: number, value) {
      //@ts-ignore
      if (isNaN(value[type][item])) {
        return sum;
      }
      //@ts-ignore
      return sum + value[type][item];
    }, 0);
    return value;
  };

  const finalObj: IUserCostResults = {
    queueLengthWarning: queueLengthWarning,
    traverseWzCosts: traverseWzCosts,
    weightedSlowCosts: weightedSlowCosts,
    weightedStoppedCosts: weightedStoppedCosts,
    vehicleCostsPerDay: weightedSlowCosts.vehicle.total + weightedStoppedCosts.vehicle.total + getQueuePeriodData('idleVocCost', 'total'),
    delayCostsPerDay: weightedSlowCosts.delay.total + weightedStoppedCosts.delay.total + getQueuePeriodData('idleDelayCost', 'total') + traverseWzCosts.total,
    queuePeriods: queuePeriods,
    totalCostsPerDay: {
      cars: weightedSlowCosts.delay.cars + weightedStoppedCosts.delay.cars + weightedSlowCosts.vehicle.cars + weightedStoppedCosts.vehicle.cars + traverseWzCosts.cars + getQueuePeriodData('idleDelayCost', 'cars') + getQueuePeriodData('idleVocCost', 'cars'),
      trucks: weightedSlowCosts.delay.trucks + weightedStoppedCosts.delay.trucks + weightedSlowCosts.vehicle.trucks + weightedStoppedCosts.vehicle.trucks + traverseWzCosts.trucks + getQueuePeriodData('idleDelayCost', 'trucks') + getQueuePeriodData('idleVocCost', 'trucks'),
      comboTrucks: weightedSlowCosts.delay.comboTrucks + weightedStoppedCosts.delay.comboTrucks + weightedSlowCosts.vehicle.comboTrucks + weightedStoppedCosts.vehicle.comboTrucks + traverseWzCosts.comboTrucks + getQueuePeriodData('idleDelayCost', 'comboTrucks') + getQueuePeriodData('idleVocCost', 'comboTrucks'),
      total: weightedSlowCosts.delay.total + weightedStoppedCosts.delay.total + weightedSlowCosts.vehicle.total + weightedStoppedCosts.vehicle.total + traverseWzCosts.total + getQueuePeriodData('idleDelayCost', 'total') + getQueuePeriodData('idleVocCost', 'total')
    },
    totalCosts: {
      cars: 0,
      trucks: 0,
      comboTrucks: 0,
      total: 0,
    },
    vehicleCostsTotal: 0,
    delayCostsTotal: 0,
    discountedCosts: {
      cars: 0,
      trucks: 0,
      comboTrucks: 0,
      total: 0,
    },
  };

  const discountRate = (0.01 * scenario.discountRate);


  finalObj.totalCosts = {
    cars: (finalObj.totalCostsPerDay.cars * workzoneDuration) || 0,
    trucks: (finalObj.totalCostsPerDay.trucks * workzoneDuration) || 0,
    comboTrucks: (finalObj.totalCostsPerDay.comboTrucks * workzoneDuration) || 0,
    total: (finalObj.totalCostsPerDay.total * workzoneDuration) || 0
  };

  // console.log('totalcosts', activity.name, finalObj.totalCosts.total);

  finalObj.vehicleCostsTotal = finalObj.vehicleCostsPerDay * workzoneDuration;
  finalObj.delayCostsTotal = finalObj.delayCostsPerDay * workzoneDuration;

  finalObj.discountedCosts = {
    // cars: finalObj.totalCosts.cars,
    // trucks: finalObj.totalCosts.trucks,
    // comboTrucks: finalObj.totalCosts.comboTrucks,
    cars: finalObj.totalCosts.cars * (Math.pow((1 + discountRate), analysisPeriod)),
    trucks: finalObj.totalCosts.trucks * (Math.pow((1 + discountRate), analysisPeriod)),
    comboTrucks: finalObj.totalCosts.comboTrucks * (Math.pow((1 + discountRate), analysisPeriod)),
    total: finalObj.totalCosts.total * (Math.pow((1 + discountRate), analysisPeriod))
  };

  // for(k = 0; k <= analysisPeriod; k++) {
  //     finalObj.discountedCosts.cars =  finalObj.discountedCosts.cars/(Math.pow((1+discountRate_pct),k));
  //     finalObj.discountedCosts.trucks =  finalObj.discountedCosts.cars/(Math.pow((1+discountRate_pct),k));
  //     finalObj.discountedCosts.comboTrucks =  finalObj.discountedCosts.cars/(Math.pow((1+discountRate_pct),k));
  //     finalObj.discountedCosts.total +=  finalObj.totalCosts.total/(Math.pow((1+discountRate_pct),k));
  // }
  return finalObj;
}
